/* eslint-disable no-nested-ternary -- disable nested ternary for catalog carousel */
import type { Catalog as CatalogType } from "@repo/fetch-repository/catalogs";
import { CatalogRepository } from "@repo/fetch-repository/catalogs";
import type { AutoplayType } from "embla-carousel-autoplay";
import Autoplay from "embla-carousel-autoplay";
import { forwardRef, useEffect, useRef, useState } from "react";
import type { ComponentType } from "@repo/editor/types";
import type {
  ComponentSettings,
  ComponentSettingsProps,
} from "@repo/editor/externalComponents";
import * as t from "@rekajs/types";
import { useRouter } from "next/router";
import type { ErrorType } from "@repo/fetch-repository/factory";
import { useApp } from "@repo/app/useApp";
import { observer } from "mobx-react-lite";
import { Link } from "@repo/i18n/routing";
import { useTranslations } from "next-intl";
import { Skeleton } from "../components/ui/skeleton";
import { Download } from "../components/icons/download";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "../components/ui/carousel";
import { Button } from "../components/ui/button";

const CatalogueCard = observer(
  (props: CatalogType): React.JSX.Element | null => {
    const app = useApp();
    const isEditor = app.rendererMode === "editor";
    return !Array.isArray(props.cover.data) &&
      !Array.isArray(props.file.data) ? (
      <div className="flex flex-col gap-y-9">
        <div className="flex flex-col gap-y-4">
          <img
            alt=""
            className="w-full h-full max-h-[420.6px] md:max-h[570px] lg:max-h[646px] object-contain"
            onError={(e) => {
              e.currentTarget.src = "/images/placeholder.png";
            }}
            src={props.cover.data.url}
          />
          <div className="pt-3">
            <h3 className="font-helvetica rtl:font-doran lg:text-xl text-base text-[#4C4C4C] font-[100] rtl:font-medium leading-[1.07] tracking-[-0.24px] mb-2">
              {props.title}
            </h3>
            <p className="font-helvetica rtl:font-doran lg:text-base text-sm text-[#6a6e77] font-[100] rtl:font-medium leading-[1.83] tracking-[0.0px]">
              {props.description}
            </p>
          </div>
          <div className="flex gap-x-2">
            {/* View Dialog */}
            <a
              download
              href={props.file.data.url}
              onClick={(e) => {
                if (isEditor) {
                  e.preventDefault();
                }
              }}
              rel="noreferrer"
              target="_blank"
            >
              <Download />
            </a>
          </div>
        </div>
      </div>
    ) : null;
  }
);

interface CatalogCarouselProps {
  title: string;
}

export const CataloguesDefinition: ComponentSettingsProps = {
  title: {
    type: "string",
    label: "عنوان",
  },
};

export const CatalogCarousel = forwardRef<HTMLDivElement, CatalogCarouselProps>(
  (props, ref) => {
    const router = useRouter();
    const app = useApp();
    const translate = useTranslations();
    const isEditor = app.rendererMode === "editor";
    const locale =
      app.rendererMode === "editor"
        ? (router.query.locale as string)
        : router.locale || "en";
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ErrorType | null>(null);
    const [items, setItems] = useState<CatalogType[] | null>(null);
    const plugin = useRef<AutoplayType>(
      Autoplay({
        delay: 5000,
      })
    );
    useEffect(() => {
      setLoading(true);
      const repository: CatalogRepository = new CatalogRepository(locale);
      void repository
        .getMany()
        .then((res) => {
          if (res.succeeded && res.data) {
            setItems(res.data);
          } else {
            setItems(null);
            setError({
              message: res.errors?.message ?? "Unknown error",
              status: res.errors?.status ?? 500,
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }, [locale]);
    return (
      <div ref={ref}>
        {loading ? (
          <div className="flex flex-col gap-y-9">
            <Skeleton className="max-w-56 w-full h-10 rounded-xl" />
            <div className="w-full h-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-20">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  className="w-full flex flex-col items-start justify-start gap-y-4"
                  key={index}
                >
                  <Skeleton className="w-full h-[200px] rounded-xl" />
                  <Skeleton className="w-full h-10 rounded-xl" />
                  {Array.from({ length: 2 }).map((__, index2) => (
                    <Skeleton className="w-full h-4 rounded-xl" key={index2} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : !error ? (
          <div className="w-full h-full flex flex-col gap-y-9">
            <div className="flex justify-between items-center">
              <h1 className="font-helvetica rtl:font-doran text-[25px] md:text-2xl lg:text-3xl font-[100] rtl:font-medium leading-[1.07] text-[#131D29] uppercase">
                {props.title}
              </h1>
              {!isEditor ? (
                <Link href="/catalogs">
                  <Button variant="outline">
                    {translate("catalogs.more")}
                  </Button>
                </Link>
              ) : null}
            </div>
            <Carousel
              className="w-full"
              dir="ltr"
              onMouseEnter={plugin.current.stop}
              onMouseLeave={plugin.current.reset}
              plugins={[plugin.current]}
            >
              <CarouselContent>
                {items?.map((catalogue) => (
                  <CarouselItem
                    className="lg:basis-1/4 md:basis-1/3 basis-full p-6"
                    dir="auto"
                    key={catalogue.id}
                  >
                    <CatalogueCard key={catalogue.id} {...catalogue} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        ) : (
          <div className="w-full h-full flex flex-col gap-y-9">
            <h1 className="font-helvetica rtl:font-doran text-[25px] md:text-2xl lg:text-3xl font-[100] rtl:font-medium leading-[1.07] text-[#131D29] uppercase">
              {props.title}
            </h1>
          </div>
        )}
      </div>
    );
  }
) as ComponentType<CatalogCarouselProps>;
CatalogCarousel.displayName = "CatalogCarousel";

export const CatalogCarouselSettings: ComponentSettings = {
  name: "ECatalogCarousel",
  props: CataloguesDefinition,
  label: "کاتالوگ کاروسل",
  childrenAllowed: false,
  widgets: {
    label: "کاتالوگ کاروسل",
    template: () =>
      t.componentTemplate({
        component: t.identifier({
          name: "ECatalogCarousel",
          external: true,
        }),
      }),
  },
};

CatalogCarousel.componentSettings = CatalogCarouselSettings;
